import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface WinnerAnnouncement {
  winnerName: string;
  winnerTeam: 'black' | 'white';
  totalScore: number;
  sessionsCount: number;
  mode: 'individual' | 'team';
  announcedAt: number;
}

const CHANNEL_NAME = 'srs-winner-broadcast';
const WINDOW_EVENT = 'srs-winner-announcement';

export function useWinnerBroadcast() {
  const [announcement, setAnnouncement] = useState<WinnerAnnouncement | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // ── Same-device listener ──────────────────────────────────────────────────
  // Supabase broadcast doesn't self-echo, so we use a window CustomEvent
  // to sync all hook instances on the same device.
  useEffect(() => {
    const handler = (e: Event) =>
      setAnnouncement((e as CustomEvent<WinnerAnnouncement>).detail);
    window.addEventListener(WINDOW_EVENT, handler);
    return () => window.removeEventListener(WINDOW_EVENT, handler);
  }, []);

  // ── Cross-device receiver via Supabase Realtime Broadcast ─────────────────
  useEffect(() => {
    const ch = supabase.channel(CHANNEL_NAME, { config: { broadcast: { ack: false } } });
    ch.on('broadcast', { event: 'announce_winner' }, (msg) => {
      setAnnouncement(msg.payload as WinnerAnnouncement);
    }).subscribe();
    channelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
      channelRef.current = null;
    };
  }, []);

  const announceWinner = useCallback((data: Omit<WinnerAnnouncement, 'announcedAt'>) => {
    const payload: WinnerAnnouncement = { ...data, announcedAt: Date.now() };

    // Fire window event so ALL hook instances on this device update immediately
    window.dispatchEvent(new CustomEvent(WINDOW_EVENT, { detail: payload }));

    // Push to all other connected devices via Supabase Realtime
    channelRef.current?.send({
      type: 'broadcast',
      event: 'announce_winner',
      payload,
    });
  }, []);

  const dismissAnnouncement = useCallback(() => {
    setAnnouncement(null);
  }, []);

  return { announcement, announceWinner, dismissAnnouncement };
}
